import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useSeo from "../seo/useSeo";
import useJsonLd from "../seo/useJsonLd";
import ActionCard from "../components/ActionCard";

export default function ConversionPage() {
  const navigate = useNavigate();

  /* ================= SEO ================= */
  useSeo({
    title: "Conversion PDF & Images – PDF→JPG/PNG, JPG→PDF | pdf-convert.fr",
    description:
      "Convertissez un PDF en JPG/PNG, transformez des images en PDF A4 et compressez vos images. Outils gratuits, rapides et 100% locaux (sans envoi).",
    canonical: "https://pdf-convert.fr/conversion",
  });

  /* ================= BREADCRUMB ================= */
  const breadcrumbJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Accueil",
          item: "https://pdf-convert.fr/home",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Conversion",
          item: "https://pdf-convert.fr/conversion",
        },
      ],
    }),
    []
  );
  useJsonLd("breadcrumb-conversion", breadcrumbJsonLd);

  /* ================= FAQ ================= */
  const faqItems = useMemo(
    () => [
      {
        q: "Est-ce que les fichiers sont envoyés sur un serveur ?",
        a: "Non. Les conversions et compressions se font directement dans votre navigateur. Aucun fichier n’est envoyé ni stocké.",
      },
      {
        q: "Quelle est la différence entre PDF→JPG et PDF→PNG ?",
        a: "Le JPG est souvent plus léger, idéal pour partager. Le PNG est sans perte et conserve plus de détails, mais pèse généralement plus lourd.",
      },
      {
        q: "Puis-je convertir plusieurs images en un seul PDF ?",
        a: "Oui. Vous pouvez sélectionner plusieurs images et générer un PDF A4, avec un zoom maximal sans déformation.",
      },
      {
        q: "Pourquoi certains fichiers sont plus lents à convertir ?",
        a: "La vitesse dépend de la taille du fichier et des performances de votre appareil. Sur ordinateur, c’est généralement plus rapide.",
      },
    ],
    []
  );

  const faqJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    }),
    [faqItems]
  );
  useJsonLd("faq-jsonld-conversion", faqJsonLd);

  return (
    <main style={{ maxWidth: 900, margin: "0 auto", padding: 16 }}>
      <h1>Conversion PDF & Images</h1>

      {/* === TEXTE SEO VISIBLE (important) === */}
      <p
        style={{
          textAlign: "center",
          color: "#666",
          maxWidth: 760,
          margin: "12px auto 22px",
          lineHeight: 1.5,
        }}
      >
        Sur <strong>pdf-convert.fr</strong>, vous pouvez convertir vos fichiers{" "}
        <strong>PDF en images</strong> (JPG ou PNG), transformer plusieurs{" "}
        <strong>images en PDF A4</strong>, et réduire le poids de vos images.
        Tout fonctionne <strong>directement dans votre navigateur</strong> :
        aucun fichier n’est envoyé sur un serveur.
      </p>

      {/* === CARTES (même style partout) === */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 20,
          marginTop: 18,
        }}
      >
        <ActionCard
          title="PDF → JPG"
          description="Convertir un PDF en images JPG (souvent plus léger)"
          onClick={() => navigate("/pdf-jpg")}
        />

        <ActionCard
          title="PDF → PNG"
          description="Convertir un PDF en images PNG (qualité maximale)"
          onClick={() => navigate("/pdf-png")}
        />

        <ActionCard
          title="JPG / PNG→ PDF"
          description="Regrouper une ou plusieurs images en PDF A4"
          onClick={() => navigate("/jpg-pdf")}
        />
      </div>

      {/* === LIENS UTILES / PROLONGEMENTS (bon pour SEO + UX) === */}
      <div style={{ marginTop: 26, textAlign: "center", color: "#666" }}>
        <p style={{ margin: 0 }}>
          Besoin de regrouper des fichiers ?{" "}
          <a href="/fusion" style={{ textDecoration: "underline" }}>
            Accéder à la page Fusion
          </a>
          .
        </p>

        <p style={{ marginTop: 10 }}>
          Pour plus d’informations, consultez la{" "}
          <a href="/faq" style={{ textDecoration: "underline" }}>
            FAQ
          </a>{" "}
          ou la{" "}
          <a href="/confidentialite" style={{ textDecoration: "underline" }}>
            politique de confidentialité
          </a>
          .
        </p>
      </div>
    </main>
  );
}
